import { useState } from "react";
import Icon from "./Icon";
import { GlassCard, Btn } from "./UI";
import {
  useStoreMutation,
  mutations,
  type SeasonKind,
  type Trimester,
  type FeedingMode,
  type SeasonState,
} from "@/lib/store";

/**
 * Modal-style sheet that lets a sister set her season. Lives inline (parent
 * controls open/close). Postpartum movement clearance is honored if Kiley
 * already set it.
 */
interface Props {
  userId: string;
  current: SeasonState | null;
  onClose: () => void;
}

export default function SeasonSetup({ userId, current, onClose }: Props) {
  const [kind, setKind] = useState<SeasonKind>(current?.kind ?? "none");
  const [trimester, setTrimester] = useState<Trimester>((current?.trimester ?? 2) as Trimester);
  const [weeksPostpartum, setWeeksPostpartum] = useState<number>(current?.weeksPostpartum ?? 6);
  const [feeding, setFeeding] = useState<FeedingMode>(current?.feeding ?? "exclusive");
  const [milestoneDate, setMilestoneDate] = useState<string>(current?.milestoneDate ?? "");
  const save = useStoreMutation(mutations.saveSeasonState);

  const onSave = () => {
    save({
      userId,
      kind,
      trimester: kind === "pregnant" ? trimester : null,
      weeksPostpartum: kind === "postpartum" ? weeksPostpartum : null,
      feeding: kind === "nursing" ? feeding : "none",
      clearedForMovement: current?.clearedForMovement ?? (kind !== "postpartum"),
      milestoneDate: milestoneDate || null,
    });
    onClose();
  };

  return (
    <div
      className="fixed inset-0 flex items-end justify-center"
      style={{ background: "rgba(22,56,40,0.55)", zIndex: 50 }}
      onClick={onClose}
      data-testid="season-setup-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 420,
          background: "var(--mnf-cream)",
          borderRadius: "24px 24px 0 0",
          padding: 24,
          maxHeight: "82vh",
          overflowY: "auto",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Honor your season</p>
            <h2 className="mnf-serif" style={{ fontSize: 24, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.15 }}>
              What season are you in?
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="button-close-season-setup"
            className="hover-elevate"
            style={{ width: 36, height: 36, borderRadius: 999, background: "rgba(30,74,53,0.06)", color: "var(--mnf-f)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            <Icon name="close" size={18} />
          </button>
        </div>

        <p className="mnf-body" style={{ fontSize: 13, color: "var(--mnf-tg)", lineHeight: 1.55, marginBottom: 18 }}>
          Your macros, movement, and Scripture pairings will adjust to walk with you in this season. Coach Kiley is GGS Pre/Postnatal V2 certified.
        </p>

        <div className="space-y-2 mb-5">
          {KINDS.map((k) => (
            <KindRow key={k.value} active={kind === k.value} onClick={() => setKind(k.value)} {...k} />
          ))}
        </div>

        {kind === "pregnant" && (
          <GlassCard padding={16}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginBottom: 8 }}>Trimester</p>
            <div className="flex gap-2 mb-3">
              {[1, 2, 3].map((t) => (
                <SegBtn key={t} active={trimester === t} onClick={() => setTrimester(t as Trimester)}>T{t}</SegBtn>
              ))}
            </div>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginBottom: 8 }}>Estimated due date</p>
            <input
              type="date"
              value={milestoneDate}
              onChange={(e) => setMilestoneDate(e.target.value)}
              data-testid="input-due-date"
              className="mnf-body w-full"
              style={{ padding: "10px 12px", borderRadius: 12, border: "1px solid rgba(30,74,53,0.16)", background: "#fff", color: "var(--mnf-fd)", fontSize: 14 }}
            />
          </GlassCard>
        )}

        {kind === "postpartum" && (
          <GlassCard padding={16}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginBottom: 8 }}>Weeks postpartum</p>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={26}
                value={weeksPostpartum}
                onChange={(e) => setWeeksPostpartum(parseInt(e.target.value))}
                data-testid="input-weeks-postpartum"
                className="flex-1"
              />
              <span className="mnf-serif" style={{ fontSize: 22, fontWeight: 500, color: "var(--mnf-fd)", minWidth: 40, textAlign: "right" }}>{weeksPostpartum}</span>
            </div>
            <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 10, lineHeight: 1.5 }}>
              Movement returns when Coach Kiley clears you. Until then, breath work and walking only.
            </p>
          </GlassCard>
        )}

        {kind === "nursing" && (
          <GlassCard padding={16}>
            <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)", marginBottom: 8 }}>How are you feeding</p>
            <div className="flex gap-2">
              <SegBtn active={feeding === "exclusive"} onClick={() => setFeeding("exclusive")}>Exclusive</SegBtn>
              <SegBtn active={feeding === "mixed"} onClick={() => setFeeding("mixed")}>Mixed</SegBtn>
            </div>
            <p className="mnf-body italic" style={{ fontSize: 12, color: "var(--mnf-tg)", marginTop: 10, lineHeight: 1.5 }}>
              Exclusive nursing adds about 475 kcal/day. Mixed feeding adds about 275 kcal/day.
            </p>
          </GlassCard>
        )}

        <div className="flex gap-2 mt-5">
          <Btn variant="ghost" size="md" onClick={onClose}>Not yet</Btn>
          <Btn variant="primary" size="md" onClick={onSave} className="flex-1" data-testid="button-save-season">
            Save my season
          </Btn>
        </div>
      </div>
    </div>
  );
}

const KINDS: Array<{ value: SeasonKind; title: string; sub: string; icon: string }> = [
  { value: "none",       title: "Not in a season",       sub: "Standard macros and Garden flows.",                            icon: "spa" },
  { value: "ttc",        title: "Trying to conceive",    sub: "Protein and Scripture for hopeful waiting.",                   icon: "favorite" },
  { value: "pregnant",   title: "Pregnant",              sub: "Trimester aware kcal and core cues.",                          icon: "child_friendly" },
  { value: "postpartum", title: "Postpartum recovery",   sub: "Gentle return-to-movement, gated by Kiley.",                   icon: "self_care" },
  { value: "nursing",    title: "Nursing",               sub: "Energy and protein bumps for milk supply.",                    icon: "water_drop" },
];

function KindRow({ value: _value, title, sub, icon, active, onClick }: { value: SeasonKind; title: string; sub: string; icon: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      data-testid={`button-season-${_value}`}
      className="w-full flex items-center gap-3 hover-elevate"
      style={{
        padding: "12px 14px",
        borderRadius: 16,
        border: active ? "2px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.10)",
        background: active ? "rgba(168,212,184,0.18)" : "#fff",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: "inherit",
      }}
    >
      <span
        style={{ width: 36, height: 36, borderRadius: 999, background: active ? "var(--mnf-f)" : "rgba(30,74,53,0.08)", color: active ? "#F5F0E8" : "var(--mnf-f)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
      >
        <Icon name={icon} size={18} weight={500} />
      </span>
      <span className="flex-1 min-w-0">
        <span className="mnf-serif block" style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)" }}>{title}</span>
        <span className="mnf-body block" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}>{sub}</span>
      </span>
      {active && <Icon name="check" size={16} color="var(--mnf-f)" />}
    </button>
  );
}

function SegBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="hover-elevate flex-1"
      style={{
        padding: "8px 12px",
        borderRadius: 10,
        border: active ? "1px solid var(--mnf-f)" : "1px solid rgba(30,74,53,0.16)",
        background: active ? "var(--mnf-f)" : "#fff",
        color: active ? "#F5F0E8" : "var(--mnf-fd)",
        fontFamily: "inherit",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}
